import type {
  FoundationComponentEntityTable,
  FoundationComponentId,
} from "building-types/foundations/foundationComponentEntityTypes";
import { FoundationComponentType } from "building-types/foundations/foundationComponentEntityTypes";
import { autoWidthCurbNew } from "building-types/foundations/foundationComponents";
import {
  BrickLedgeBehavior,
  BrickLedgeType,
  FoundationSubSystemType,
} from "building-types/foundations/foundationEnums";
import { assertUnreachable } from "common/utils/assertUnreachable";
import {
  stripFootingNew,
  turndownFootingNew,
  circularIsolatedFootingNew,
  rectangularIsolatedFootingNew,
  cipStemWallNew,
  cmuStemWallNew,
  circularCIPPierNew,
  rectangularCIPPierNew,
  rectangularCMUPierNew,
} from "./foundationComponentEntity";

export const defaultStripFootingId: FoundationComponentId = "defaultStripFooting";
export const defaultTurndownFootingId: FoundationComponentId = "defaultTurndownFooting";
export const defaultCircularIsolatedFootingId: FoundationComponentId = "defaultCircularIsolatedFooting";
export const defaultRectangularIsolatedFootingId: FoundationComponentId = "defaultRectangularIsolatedFooting";
export const defaultCIPStemWallId: FoundationComponentId = "defaultCIPStemWall";
export const defaultCMUStemWallId: FoundationComponentId = "defaultCMUStemWall";
export const defaultCircularCIPPierId: FoundationComponentId = "defaultCircularCIPPier";
export const defaultRectangularCIPPierId: FoundationComponentId = "defaultRectangularCIPPier";
export const defaultRectangularCMUPierId: FoundationComponentId = "defaultRectangularCMUPier";
export const defaultCurbId: FoundationComponentId = "defaultCurb";

const defaultFoundationComponentsInternal = {
  [defaultStripFootingId]: stripFootingNew(
    '18"x8" Strip Footing w/ AUTO 4" Brick Padding',
    18,
    8,
    4,
    defaultStripFootingId,
  ),
  [defaultTurndownFootingId]: turndownFootingNew(
    '18" Turndown Footing w/ AUTO 5.5" Ledge',
    18,
    BrickLedgeBehavior.AUTO,
    BrickLedgeType.ADD,
    5.5,
    defaultTurndownFootingId,
  ),
  [defaultCircularIsolatedFootingId]: circularIsolatedFootingNew(
    '16" Diameter Circular Isolated Footing',
    8,
    6,
    undefined,
    defaultCircularIsolatedFootingId,
  ),
  [defaultRectangularIsolatedFootingId]: rectangularIsolatedFootingNew(
    '16"x16" Rectangular Isolated Footing',
    16,
    16,
    6,
    undefined,
    defaultRectangularIsolatedFootingId,
  ),
  [defaultCIPStemWallId]: cipStemWallNew(
    '8" CIP Stem Wall w/ AUTO 5.5" Ledge',
    8,
    BrickLedgeBehavior.AUTO,
    BrickLedgeType.ADD,
    5.5,
    defaultCIPStemWallId,
  ),
  [defaultCMUStemWallId]: cmuStemWallNew(
    '8" CMU Stem Wall w/ AUTO 4" Ledge',
    8,
    BrickLedgeBehavior.AUTO,
    BrickLedgeType.ADD,
    12,
    defaultCMUStemWallId,
  ),
  [defaultCircularCIPPierId]: circularCIPPierNew(
    '8" Diameter Circular CIP Pier',
    4,
    undefined,
    undefined,
    defaultCircularCIPPierId,
  ),
  [defaultRectangularCMUPierId]: rectangularCMUPierNew(
    '8" x 8" Rectangular CMU Pier',
    8,
    8,
    undefined,
    defaultRectangularCMUPierId,
  ),
  [defaultRectangularCIPPierId]: rectangularCIPPierNew(
    '8" x 8" Rectangular CIP Pier',
    8,
    8,
    undefined,
    undefined,
    defaultRectangularCIPPierId,
  ),
  [defaultCurbId]: autoWidthCurbNew("Auto Curb", defaultCurbId),
} as const;

export const builtInFoundationComponents: FoundationComponentEntityTable = defaultFoundationComponentsInternal;

export const builtInFoundationComponentsGroupId = "builtInFoundationComponentsGroup";

export const defaultFoundationComponentEntityIds = new Set(Object.keys(builtInFoundationComponents));

export function isBuiltInFoundationComponent(id: FoundationComponentId): boolean {
  return Object.values(builtInFoundationComponents).some((component) => component.id === id);
}

export function getDefaultBuiltInFoundationComponentId(assembly: FoundationSubSystemType): FoundationComponentId {
  switch (assembly) {
    case FoundationSubSystemType.MONOLITHIC:
      return defaultTurndownFootingId;
    case FoundationSubSystemType.STEM_WALL_CIP:
      return defaultStripFootingId;
    case FoundationSubSystemType.STEM_WALL_CMU:
      return defaultStripFootingId;
    case FoundationSubSystemType.ISOLATED:
      return defaultCircularCIPPierId;
    default:
      assertUnreachable(assembly);
  }
}

function defaultMonolithicFoundationComponentId(
  foundationComponent: FoundationComponentType,
): FoundationComponentId | undefined {
  switch (foundationComponent) {
    case FoundationComponentType.FOOTING: {
      return "defaultTurndownFooting";
    }
    case FoundationComponentType.CURB: {
      return "defaultCurb";
    }
    case FoundationComponentType.PIER:
    case FoundationComponentType.STEMWALL: {
      return undefined;
    }
    default: {
      assertUnreachable(foundationComponent);
    }
  }
}

function defaultCIPStemWallFoundationComponentId(
  foundationComponent: FoundationComponentType,
): FoundationComponentId | undefined {
  switch (foundationComponent) {
    case FoundationComponentType.FOOTING: {
      return "defaultStripFooting";
    }
    case FoundationComponentType.STEMWALL: {
      return "defaultCIPStemWall";
    }
    case FoundationComponentType.CURB: {
      return "defaultCurb";
    }
    case FoundationComponentType.PIER: {
      return undefined;
    }
    default: {
      assertUnreachable(foundationComponent);
    }
  }
}

function defaultCMUStemWallFoundationComponentId(
  foundationComponent: FoundationComponentType,
): FoundationComponentId | undefined {
  switch (foundationComponent) {
    case FoundationComponentType.FOOTING: {
      return "defaultStripFooting";
    }
    case FoundationComponentType.STEMWALL: {
      return "defaultCMUStemWall";
    }
    case FoundationComponentType.CURB: {
      return "defaultCurb";
    }
    case FoundationComponentType.PIER: {
      return undefined;
    }
    default: {
      assertUnreachable(foundationComponent);
    }
  }
}

function defaultConcretePierFoundationComponentId(
  foundationComponent: FoundationComponentType,
): FoundationComponentId | undefined {
  switch (foundationComponent) {
    case FoundationComponentType.FOOTING: {
      return "defaultCircularIsolatedFooting";
    }
    case FoundationComponentType.STEMWALL: {
      return undefined;
    }
    case FoundationComponentType.CURB: {
      return undefined;
    }
    case FoundationComponentType.PIER: {
      return "defaultCircularCIPPier";
    }
    default: {
      assertUnreachable(foundationComponent);
    }
  }
}

export function defaultFoundationComponentId(
  foundationSubSystem: FoundationSubSystemType,
  foundationComponent: FoundationComponentType,
) {
  switch (foundationSubSystem) {
    case FoundationSubSystemType.MONOLITHIC: {
      return defaultMonolithicFoundationComponentId(foundationComponent);
    }
    case FoundationSubSystemType.STEM_WALL_CIP: {
      return defaultCIPStemWallFoundationComponentId(foundationComponent);
    }
    case FoundationSubSystemType.STEM_WALL_CMU: {
      return defaultCMUStemWallFoundationComponentId(foundationComponent);
    }
    case FoundationSubSystemType.ISOLATED: {
      return defaultConcretePierFoundationComponentId(foundationComponent);
    }
    default: {
      assertUnreachable(foundationSubSystem);
    }
  }
}
